import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IProduct } from '../../../interfaces'

/**
 * Status of a recommendations carousel data fetch.
 *
 * `IDLE` is the initial state — no fetch has been triggered yet. Consumers
 * (e.g. the carousel hooks added in COM-223) can distinguish "never fetched"
 * from "fetched successfully with 0 items" (`OK` + `items: []`) and decide
 * whether to trigger an initial fetch.
 *
 * @internal
 */
export type CarouselStatus = 'IDLE' | 'OK' | 'LOADING' | 'ERROR'

/**
 * Shared state shape for any recommendations carousel slice produced by
 * `createCarouselSlice`.
 * @internal
 */
export interface ICarouselState {
  items: IProduct[]
  status: CarouselStatus
  hideCarousel: boolean
}

/**
 * Default initial state for any recommendations carousel slice.
 * `hideCarousel` defaults to `true` so the UI stays hidden until items
 * arrive — preventing an empty placeholder from flashing on first render.
 *
 * Frozen + always spread at the use site: prevents accidental mutation by
 * importers and avoids aliasing in test mocks where multiple slice keys
 * would otherwise share the same object reference.
 * @internal
 */
export const carouselInitialState: Readonly<ICarouselState> = Object.freeze({
  items: [],
  status: 'IDLE',
  hideCarousel: true,
})

/**
 * Factory that returns a Redux Toolkit slice for a recommendations carousel.
 * All carousels share the same state shape and reducer set, so the only
 * differentiator is the slice `name`, which determines its key in the
 * combined reducer.
 *
 * Reducers:
 *  - `setItems(IProduct[])` — replaces the carousel items and toggles
 *    `hideCarousel` based on whether the payload is empty.
 *  - `setStatus(CarouselStatus)` — updates the fetch status.
 *
 * @param name unique slice name (also used as the reducer key in the store)
 * @internal
 */
export const createCarouselSlice = (name: string) =>
  createSlice({
    name,
    initialState: { ...carouselInitialState },
    reducers: {
      setItems(state, { payload }: PayloadAction<IProduct[]>) {
        // Defense-in-depth: the GraphQL contract declares items as non-null,
        // but a server bug returning null would crash on payload.length.
        // Coerce non-array payloads to [] so the slice stays in a valid
        // state and the carousel hides cleanly.
        const items = Array.isArray(payload) ? payload : []
        state.items = items
        state.hideCarousel = items.length === 0
      },
      setStatus(state, { payload }: PayloadAction<CarouselStatus>) {
        state.status = payload
      },
    },
  })
